import { Box, Button, Stack, styled, Skeleton, ThemeProvider, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material'
import { observer } from 'mobx-react-lite'
import { useState, useEffect } from 'react'
import { MainTemplate } from '../Template/MainTemplate'

import { ReactComponent as Plus } from './ShopifyFaqs/PlusIcon.svg'
import { ReactComponent as Minus } from './ShopifyFaqs/MinusIcon.svg'

const faqDataMap = {
    newzealand: () => import('./ShopifyFaqs/data/newzealand.json')
}

const FaqsPage = observer(({ country = 'newzealand' }) => {
    const [faqData, setFaqData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [selectedGroup, setSelectedGroup] = useState(0)
    const [expanded, setExpanded] = useState(new Set())

    useEffect(() => {
        const loadFaqData = async () => {
            setLoading(true)
            try {
                const dataLoader = faqDataMap[country.toLowerCase()]
                if (dataLoader) {
                    const module = await dataLoader()
                    setFaqData(module.default || module)
                }
            } catch (error) {
                console.error('Failed to load FAQ data:', error)
            } finally {
                setLoading(false)
            }
        }

        loadFaqData()
    }, [country])

    const handleChange = (newAlignment) => {
        setSelectedGroup(newAlignment)
        setExpanded(new Set())
    }

    const handleAccordionChange = (groupKey, itemKey) => {
        const key = `${groupKey}-${itemKey}`
        setExpanded(prev => {
            const newSet = new Set(prev)
            if (newSet.has(key)) {
                newSet.delete(key)
            } else {
                newSet.add(key)
            }
            return newSet
        })
    }

    return (
        <Stack bgcolor={'rgba(247, 250, 252, 1)'} pb="60px" pt="20px" paddingX={{
            xs: '24px',
            sm: 0
        }} minHeight={{
            xs: 'calc(100vh - (75px + 93px + 80px))',
            sm: 'calc(100vh - (75px + 64px + 80px))'
        }}>
            <ThemeProvider theme={MainTemplate}>
                {!loading && faqData ? (
                    <>
                        <Box width="80%" textAlign={'-webkit-center'} alignSelf={'center'} mt="20px">
                            <Typography variant="h6" fontSize={'32px!important'} fontWeight={700} fontFamily={'Poppins'}>{faqData.title}</Typography>
                        </Box>
                        <GroupButtonTab justifyContent={faqData.categories.length <= 2 ? 'center' : 'space-between'}>
                            {faqData.categories.map((category, key) => (
                                <ButtonTabStyle key={key} variant="outlined" isselected={selectedGroup === key ? 'true' : 'false'} onClick={() => handleChange(key)}>
                                    <Typography variant="caption" fontWeight={600} fontSize={'20px'} fontFamily={'Poppins'} textTransform={'none'} letterSpacing={'0'}>
                                        {category.name}
                                    </Typography>
                                </ButtonTabStyle>
                            ))}
                        </GroupButtonTab>
                        <ListComponentStyled>
                            {faqData.categories.map((category, groupKey) =>
                                category.items.map((item, itemKey) => {
                                    const isSelected = expanded.has(`${groupKey}-${itemKey}`)
                                    return (
                                        <AccordionStyled
                                            key={`${groupKey}-${itemKey}`}
                                            expanded={isSelected}
                                            style={{
                                                display: groupKey === selectedGroup ? 'block' : 'none',
                                                marginBottom: 16,
                                                padding: 20,
                                                backgroundColor: 'rgba(255, 255, 255, 1)',
                                                borderRadius: 12,
                                                borderWidth: isSelected ? '2px' : '1px',
                                            }}
                                            onChange={() => handleAccordionChange(groupKey, itemKey)}
                                            TransitionProps={{ unmountOnExit: true }}>
                                            <AccordionSummaryStyled isshow={isSelected.toString()} expandIcon={isSelected ? <Minus /> : <Plus />} aria-controls={`panel-${groupKey}-${itemKey}-content`} id={`panel-${groupKey}-${itemKey}-header`}>
                                                <Typography variant="body3" fontWeight={700} lineHeight={'24px'} style={{ listStyle: 'none' }} fontFamily={'Poppins'} fontSize={'18px'}>
                                                    {item.title}
                                                </Typography>
                                            </AccordionSummaryStyled>
                                            <AccordionDetailsStyled>
                                                <Typography variant="body3" lineHeight={'24px'} fontWeight={400} color="rgba(0, 0, 0, 1)" fontSize={'16px'}  fontFamily={'Inter'}>
                                                    <span dangerouslySetInnerHTML={{ __html: item.description }} />
                                                </Typography>
                                            </AccordionDetailsStyled>
                                        </AccordionStyled>
                                    )
                                })
                            )}
                        </ListComponentStyled>
                    </>
                ) : (
                    <>
                        <Box width="80%" textAlign={'-webkit-center'} alignSelf={'center'} mb="40px" mt="60px">
                            <Skeleton variant="rounded" width={'56%'} height={'40px'} sx={{ marginTop: '54px' }} />
                            <Stack display={'flex'} flexDirection={'row'} alignItems={'center'} justifyContent="space-between">
                                {Array.from(Array(4).keys()).map(num => (
                                    <Skeleton key={num + 'gbt'} variant="rounded" width={'20%'} height={'44px'} sx={{ margin: '40px 0 20px 0' }} />
                                ))}
                            </Stack>
                        </Box>
                        <ListComponentStyled>
                            {Array.from(Array(6).keys()).map(num => (
                                <Skeleton key={num + 'listWork'} variant="rounded" width={'100%'} height={'70px'} sx={{ margin: '0px 0 30px 0' }} />
                            ))}
                        </ListComponentStyled>
                    </>
                )}
            </ThemeProvider>
        </Stack>
    )
})

const AccordionStyled = styled(Accordion)`
    border: ${props => (props.isselected === 'true' ? '2px solid #99bcdf' : '1px solid #99bcdf')};
    border-radius: 6px;
    background-color: transparent;
    box-shadow: none;
    margin: 0px;
    &.MuiPaper-root.MuiAccordion-root {
        &::before {
            display: none;
        }
        &.Mui-expanded {
            margin: 0px;
        }
    }
`

const AccordionSummaryStyled = styled(AccordionSummary)`
    &.MuiButtonBase-root {
        &.MuiAccordionSummary-root {
            padding: 0;
            min-height: auto;
            .MuiAccordionSummary-content {
                padding: 0;
                margin: 0;
            }
        }
    }
`

const AccordionDetailsStyled = styled(AccordionDetails)`
    &.MuiAccordionDetails-root {
        padding: 0;
        padding-top: 24px;
    }
    ul {
        padding-left: 24px;
        margin: 0px;
    }
`

const GroupButtonTab = styled(Box)`
    display: inline-block;
    gap: 16px;
    white-space: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    width: 100%;
    margin: 0 auto;
    padding: 40px 0px 32px 0px;
    box-sizing: border-box;

    @media screen and (min-width: 730px) {
        display: flex;
        overflow-x: visible;
        padding: 40px 0;
    }

    @media screen and (min-width: 1200px) {
        width: 1066px;
    }
`

const ButtonTabStyle = styled(Button)`
    height: 44px;
    width: auto;
    margin-right: 16px;
    padding-left: 16px;
    padding-right: 16px;

    &.MuiButton-outlined {
        border: ${props => (props.isselected === 'true' ? '2px solid #99bcdf' : '1px solid #99bcdf')};
        border-radius: 8px;
    }

    @media screen and (min-width: 730px) {
        min-width: 134px;
        width: 40%;
        margin-right: 0;
    }

    @media screen and (min-width: 1200px) {
        width: fit-content;
    }
`

const ListComponentStyled = styled(Stack)`
    
    align-self: center;
    margin-bottom: 40px;
    @media screen and (min-width: 730px) {
        width: 80%;
    }

    @media screen and (min-width: 1200px) {
        width: 1066px;
    }
`

export default FaqsPage
